import React, { useEffect } from 'react';

import { useApolloClient } from '@apollo/client';

import {
  GetPdpStateDocument,
  GetPdpStateQuery,
} from '@storefront/data-commerce';
import useProductInformation from '@storefront/hooks/useProductInformation';
import { ProductOptions } from '@storefront/types/Product';
import { getRegularPrice } from '@storefront/utils/bundlePriceCalculator';
import { getShortDescription } from '@storefront/utils/prodRequests';

import { SelectionButtonOptional } from '../../selection-button/selection-button-optional';
import { SelectionButtonReqOptional } from '../../selection-button/selection-button-req-optional';
import {
  configurationState,
  removeOptions,
  updateConfigurations,
} from '../../state-management/pdp-config-state';
import { saveProductConfiguration } from '../../utils/utils';
import {
  BundleItemOptionWithLowestPrice,
  BundleProductWithLowestPrice,
  handlePriceDisplay,
} from '../product-configuration.utils';
import { useScrollContext } from '../scroll-context';
import {
  calculateDeltaPrice,
  removePaperOptions,
} from './options-columns.utils';

export type OptionsColumnsProps = {
  option: BundleItemOptionWithLowestPrice;
  skuList: string[];
  bundleOptionUid: string;
  requiredOption?: boolean;
  index?: number;
  isUIDSelected?: boolean;
  parentId?: string;
  hidden?: boolean;
  zeroDollarUid?: string;
  bundleProduct?: BundleProductWithLowestPrice;
};
export default function OptionsColumns(props: OptionsColumnsProps) {
  const {
    option,
    skuList,
    bundleOptionUid,
    requiredOption = false,
    index,
    isUIDSelected = false,
    parentId,
    hidden = false,
    zeroDollarUid = '',
    bundleProduct,
  } = props;
  const { configuration, activeSKU, bundleOptions } =
    useProductInformation(skuList);
  const { scrollNonRequiredOption, scrollRequiredOption } = useScrollContext();
  const handleScroll = requiredOption
    ? scrollRequiredOption
    : scrollNonRequiredOption;

  const client = useApolloClient();
  const {
    pdpConfig: { prices, selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });

  const handleChangeOption =
    (uid: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateValue = event.target.value;

      // Remove paper when selecting another print plan
      const configs = (configuration[activeSKU] ?? {}) as ProductOptions;
      removePaperOptions(configs, bundleOptions);

      updateConfigurations({
        configuration,
        activeSKU,
        uid,
        updateValue,
        requiredOption,
      });
      handleScroll(parentId);
    };

  const handleRemoveOption = () => {
    removeOptions({
      configuration,
      activeSKU,
      uid: bundleOptionUid,
      zeroDollarUid,
    });
    handleScroll(parentId);
  };

  useEffect(() => {
    const selectedOption = (configuration?.[activeSKU] as ProductOptions)?.[
      bundleOptionUid
    ];
    if (activeSKU && !selectedOption && option.is_default) {
      const newState = {
        ...configuration,
        [activeSKU]: {
          [bundleOptionUid]: option.uid,
        },
      };
      configurationState(newState);
      saveProductConfiguration(newState);
    }
  }, [configuration, activeSKU]);

  const optionPrice = () => {
    const { price } = handlePriceDisplay(bundleProduct, option);

    return calculateDeltaPrice({
      bundleOptions,
      prices,
      selected_options,
      price,
    });
  };

  const isChecked =
    (configuration?.[activeSKU] as ProductOptions)?.[bundleOptionUid] ===
    option.uid;
  if (hidden) return null;
  return requiredOption ? (
    <SelectionButtonReqOptional
      key={option.uid}
      uid={option.uid}
      checked={isChecked}
      popular={option.hp_is_popular === '1'}
      name={option.product.name}
      isDisabled={!activeSKU}
      onChange={handleChangeOption(bundleOptionUid)}
      bullets={index + 1}
      description={getShortDescription(option)}
      price={optionPrice()}
      data-testid="required-option-price"
    />
  ) : (
    <SelectionButtonOptional
      key={option.uid}
      uid={option.uid}
      checked={isChecked}
      name={option.product.name}
      isDisabled={!activeSKU || !isUIDSelected}
      onChange={handleChangeOption(bundleOptionUid)}
      onRemove={handleRemoveOption}
      price={getRegularPrice(option)}
    />
  );
}
